import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { getNotaireProfile } from '../../services/notairePortalService';
import { supabase } from '../../lib/supabase';
import Icon from '../../components/AppIcon';

const NotaireLoginPage = () => {
  const [mode, setMode] = useState('login');
  const [form, setForm] = useState({ email: '', password: '' });
  const [requestForm, setRequestForm] = useState({
    full_name: '', etude_name: '', city: '', license_number: '', phone: '', email: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  const { signIn, user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (user) checkNotaireAccess(user?.id);
  }, [user]);

  const checkNotaireAccess = async (userId) => {
    const { data } = await getNotaireProfile(userId);
    if (data) navigate('/notaire/dashboard', { replace: true });
  };

  const handleChange = (field) => (e) => {
    setForm(f => ({ ...f, [field]: e?.target?.value }));
    setError('');
  };

  const handleRequestChange = (field) => (e) => {
    setRequestForm(f => ({ ...f, [field]: e?.target?.value }));
    setError('');
  };

  const handleLogin = async (e) => {
    e?.preventDefault();
    if (!form?.email || !form?.password) { setError('Veuillez remplir tous les champs.'); return; }
    setLoading(true); setError('');
    const { data, error: err } = await signIn(form?.email, form?.password);
    if (err) { setError(typeof err === 'string' ? err : err?.message || 'Erreur de connexion.'); setLoading(false); return; }
    const userId = data?.user?.id;
    if (userId) {
      const { data: notaireData } = await getNotaireProfile(userId);
      if (notaireData) {
        navigate('/notaire/dashboard', { replace: true });
      } else {
        await supabase?.auth?.signOut();
        setError("Accès refusé. Ce portail est réservé aux notaires partenaires vérifiés.");
      }
    }
    setLoading(false);
  };

  const handleAccessRequest = async (e) => {
    e?.preventDefault();
    const { full_name, etude_name, city, license_number, email } = requestForm;
    if (!full_name || !etude_name || !city || !license_number || !email) { setError('Veuillez remplir tous les champs obligatoires.'); return; }
    setLoading(true); setError('');
    try {
      const { error: insertError } = await supabase?.from('notaire_access_requests')?.insert([{ ...requestForm, status: 'en_attente' }]);
      if (insertError) { setError(insertError?.message || 'Erreur lors de la soumission.'); }
      else {
        setSuccess("Votre demande a bien été reçue. L'équipe Kapital States vous contactera sous 48h.");
        setMode('login');
        setRequestForm({ full_name: '', etude_name: '', city: '', license_number: '', phone: '', email: '' });
      }
    } catch { setError('Impossible de soumettre la demande. Veuillez réessayer.'); }
    setLoading(false);
  };

  const requestFields = [
    { field: 'full_name', label: 'Nom complet', required: true, placeholder: 'Maître Jean Dupont', icon: 'User', type: 'text' },
    { field: 'etude_name', label: "Nom de l\'étude", required: true, placeholder: 'Étude Notariale Dupont', icon: 'Building', type: 'text' },
    { field: 'city', label: 'Ville', required: true, placeholder: 'Abidjan', icon: 'MapPin', type: 'text' },
    { field: 'license_number', label: "Numéro d\'agrément", required: true, placeholder: 'NOT-CI-2024-XXXX', icon: 'Hash', type: 'text' },
    { field: 'phone', label: 'Téléphone', required: false, placeholder: '+225 07 XX XX XX XX', icon: 'Phone', type: 'tel' },
    { field: 'email', label: 'Email professionnel', required: true, placeholder: 'notaire@etude.ci', icon: 'Mail', type: 'email' },
  ];

  return (
    <>
      <Helmet>
        <title>Espace Notaire – Kapital States</title>
        <meta name="description" content="Portail de connexion dédié aux notaires partenaires Kapital States." />
        <meta name="robots" content="noindex" />
      </Helmet>
      <div className="min-h-screen flex">
        {/* ── LEFT PANEL ── */}
        <div className="hidden lg:flex lg:w-[45%] xl:w-[42%] relative flex-col justify-between overflow-hidden"
          style={{ background: 'linear-gradient(155deg, #1a0a00 0%, #3d1a00 40%, #78350f 75%, #d97706 100%)' }}>

          {/* Noise texture overlay */}
          <div className="absolute inset-0 opacity-[0.03]"
            style={{ backgroundImage: 'url("data:image/svg+xml,%3Csvg viewBox=\'0 0 256 256\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cfilter id=\'noise\'%3E%3CfeTurbulence type=\'fractalNoise\' baseFrequency=\'0.9\' numOctaves=\'4\' stitchTiles=\'stitch\'/%3E%3C/filter%3E%3Crect width=\'100%25\' height=\'100%25\' filter=\'url(%23noise)\'/%3E%3C/svg%3E")', backgroundSize: '200px' }} />

          {/* Decorative circles */}
          <div className="absolute -top-24 -left-24 w-80 h-80 rounded-full opacity-10"
            style={{ background: 'radial-gradient(circle, #d97706, transparent 70%)' }} />
          <div className="absolute bottom-0 right-0 w-[500px] h-[500px] rounded-full opacity-[0.07]"
            style={{ background: 'radial-gradient(circle, #d97706, transparent 60%)' }} />
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] rounded-full opacity-[0.04]"
            style={{ background: 'radial-gradient(circle, #ffffff, transparent 65%)' }} />

          {/* Top nav */}
          <div className="relative z-10 p-8">
            <Link to="/homepage" className="flex items-center space-x-3 group w-fit">
              <div className="w-10 h-10 rounded-xl flex items-center justify-center"
                style={{ background: 'rgba(217,119,6,0.25)', border: '1px solid rgba(217,119,6,0.4)' }}>
                <Icon name="Building2" size={20} color="#d97706" strokeWidth={2} />
              </div>
              <div>
                <p className="text-white font-semibold text-base leading-tight tracking-tight">Kapital States</p>
                <p className="text-xs leading-tight" style={{ color: 'rgba(217,119,6,0.8)' }}>Espace Notaire</p>
              </div>
            </Link>
          </div>

          {/* Center content */}
          <div className="relative z-10 px-10 py-8">
            {/* Badge */}
            <div className="inline-flex items-center space-x-2 px-3.5 py-1.5 rounded-full mb-8"
              style={{ background: 'rgba(217,119,6,0.15)', border: '1px solid rgba(217,119,6,0.3)' }}>
              <div className="w-1.5 h-1.5 rounded-full bg-amber-400 animate-pulse" />
              <span className="text-xs font-semibold tracking-widest uppercase" style={{ color: '#d97706' }}>
                Portail Partenaires
              </span>
            </div>

            <h2 className="text-4xl xl:text-5xl font-bold text-white leading-[1.1] mb-5 tracking-tight">
              Le droit<br />
              <span style={{ color: '#d97706' }}>immobilier</span><br />
              simplifié.
            </h2>
            <p className="text-base leading-relaxed mb-10" style={{ color: 'rgba(255,255,255,0.55)' }}>
              Gérez vos dossiers, suivez les transactions et collaborez avec vos clients depuis un espace sécurisé dédié aux notaires.
            </p>

            {/* Feature list */}
            <div className="space-y-4">
              {[
                { icon: 'FolderOpen', text: 'Gestion centralisée des dossiers' },
                { icon: 'FileCheck', text: 'Suivi des actes en temps réel' },
                { icon: 'Users', text: 'Collaboration client simplifiée' },
              ]?.map(item => (
                <div key={item?.text} className="flex items-center space-x-3.5">
                  <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0"
                    style={{ background: 'rgba(217,119,6,0.2)', border: '1px solid rgba(217,119,6,0.25)' }}>
                    <Icon name={item?.icon} size={15} color="#d97706" />
                  </div>
                  <span className="text-sm font-medium" style={{ color: 'rgba(255,255,255,0.75)' }}>{item?.text}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Bottom trust strip */}
          <div className="relative z-10 px-10 pb-8">
            <div className="flex items-center space-x-6 pt-6" style={{ borderTop: '1px solid rgba(255,255,255,0.08)' }}>
              {[
                { icon: 'ShieldCheck', label: 'SSL 256-bit' },
                { icon: 'Lock', label: 'Chiffré' },
                { icon: 'Scale', label: 'RGPD' },
              ]?.map(item => (
                <div key={item?.label} className="flex items-center space-x-1.5">
                  <Icon name={item?.icon} size={13} color="rgba(217,119,6,0.7)" />
                  <span className="text-xs" style={{ color: 'rgba(255,255,255,0.4)' }}>{item?.label}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* ── RIGHT PANEL ── */}
        <div className="flex-1 flex flex-col bg-white">
          {/* Mobile header */}
          <div className="lg:hidden flex items-center justify-between px-5 py-4 border-b border-gray-100">
            <Link to="/homepage" className="flex items-center space-x-2.5">
              <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #d97706, #78350f)' }}>
                <Icon name="Building2" size={16} color="white" strokeWidth={2.5} />
              </div>
              <span className="font-bold text-sm text-gray-900">Kapital States</span>
            </Link>
            <Link to="/homepage" className="flex items-center space-x-1.5 text-sm text-gray-500 hover:text-gray-800 transition-colors">
              <Icon name="ArrowLeft" size={14} />
              <span>Retour</span>
            </Link>
          </div>

          {/* Form area */}
          <div className="flex-1 flex items-center justify-center px-6 py-10 lg:py-0">
            <div className="w-full max-w-[420px]">

              {/* Header */}
              <div className="mb-8">
                <div className="w-12 h-12 rounded-2xl flex items-center justify-center mb-5"
                  style={{ background: 'linear-gradient(135deg, rgba(217,119,6,0.12), rgba(120,53,15,0.08))', border: '1px solid rgba(217,119,6,0.2)' }}>
                  <Icon name={mode === 'login' ? 'ShieldCheck' : 'ClipboardList'} size={22} color="#78350f" />
                </div>
                <h1 className="text-2xl font-bold text-gray-900 tracking-tight mb-1.5">
                  {mode === 'login' ? 'Connexion Notaire' : "Demande d'accès"}
                </h1>
                <p className="text-sm text-gray-500 leading-relaxed">
                  {mode === 'login' ?'Accédez à votre espace sécurisé de gestion des dossiers.' :'Rejoignez le réseau notaires Kapital States.'}
                </p>
              </div>

              {/* Tab switcher */}
              <div className="flex p-1 rounded-xl mb-7" style={{ background: '#f3f4f6' }}>
                {[
                  { key: 'login', label: 'Connexion' },
                  { key: 'request', label: "Demande d'accès" },
                ]?.map(tab => (
                  <button
                    key={tab?.key}
                    onClick={() => { setMode(tab?.key); setError(''); setSuccess(''); }}
                    className="flex-1 py-2.5 text-sm font-semibold rounded-lg transition-all duration-200"
                    style={mode === tab?.key
                      ? { background: 'white', color: '#78350f', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }
                      : { color: '#6b7280' }}>
                    {tab?.label}
                  </button>
                ))}
              </div>

              {/* Alerts */}
              {success && (
                <div className="flex items-start space-x-3 p-4 rounded-xl mb-5" style={{ background: '#f0fdf4', border: '1px solid #bbf7d0' }}>
                  <Icon name="CheckCircle2" size={16} color="#16a34a" className="flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-green-700 leading-relaxed">{success}</p>
                </div>
              )}
              {error && (
                <div className="flex items-start space-x-3 p-4 rounded-xl mb-5" style={{ background: '#fff7ed', border: '1px solid #fed7aa' }}>
                  <Icon name="AlertTriangle" size={16} color="#ea580c" className="flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-orange-700 leading-relaxed">{error}</p>
                </div>
              )}

              {/* LOGIN FORM */}
              {mode === 'login' && (
                <form onSubmit={handleLogin} className="space-y-5">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Email professionnel
                    </label>
                    <div className="relative">
                      <div className="absolute left-3.5 top-1/2 -translate-y-1/2 pointer-events-none">
                        <Icon name="Mail" size={16} color="#9ca3af" />
                      </div>
                      <input
                        type="email"
                        value={form?.email}
                        onChange={handleChange('email')}
                        placeholder="notaire@etude.ci"
                        autoComplete="email"
                        className="w-full pl-10 pr-4 py-3 text-sm text-gray-900 rounded-xl transition-all duration-200 outline-none"
                        style={{ border: '1.5px solid #e5e7eb', background: '#fafafa' }}
                        onFocus={e => { e.target.style.borderColor = '#d97706'; e.target.style.background = '#fff'; e.target.style.boxShadow = '0 0 0 3px rgba(217,119,6,0.08)'; }}
                        onBlur={e => { e.target.style.borderColor = '#e5e7eb'; e.target.style.background = '#fafafa'; e.target.style.boxShadow = 'none'; }}
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Mot de passe
                    </label>
                    <div className="relative">
                      <div className="absolute left-3.5 top-1/2 -translate-y-1/2 pointer-events-none">
                        <Icon name="Lock" size={16} color="#9ca3af" />
                      </div>
                      <input
                        type={showPassword ? 'text' : 'password'}
                        value={form?.password}
                        onChange={handleChange('password')}
                        placeholder="••••••••••"
                        autoComplete="current-password"
                        className="w-full pl-10 pr-11 py-3 text-sm text-gray-900 rounded-xl transition-all duration-200 outline-none"
                        style={{ border: '1.5px solid #e5e7eb', background: '#fafafa' }}
                        onFocus={e => { e.target.style.borderColor = '#d97706'; e.target.style.background = '#fff'; e.target.style.boxShadow = '0 0 0 3px rgba(217,119,6,0.08)'; }}
                        onBlur={e => { e.target.style.borderColor = '#e5e7eb'; e.target.style.background = '#fafafa'; e.target.style.boxShadow = 'none'; }}
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(v => !v)}
                        className="absolute right-3.5 top-1/2 -translate-y-1/2 p-0.5 transition-colors"
                        style={{ color: '#9ca3af' }}>
                        <Icon name={showPassword ? 'EyeOff' : 'Eye'} size={16} />
                      </button>
                    </div>
                  </div>

                  <button
                    type="submit"
                    disabled={loading}
                    className="w-full flex items-center justify-center space-x-2.5 py-3.5 rounded-xl text-sm font-bold text-white transition-all duration-200 mt-2 disabled:opacity-60"
                    style={{ background: loading ? '#78350f' : 'linear-gradient(135deg, #78350f 0%, #92400e 50%, #d97706 100%)', boxShadow: '0 4px 14px rgba(120,53,15,0.35)' }}
                    onMouseEnter={e => { if (!loading) e.currentTarget.style.boxShadow = '0 6px 20px rgba(120,53,15,0.45)'; }}
                    onMouseLeave={e => { e.currentTarget.style.boxShadow = '0 4px 14px rgba(120,53,15,0.35)'; }}>
                    {loading
                      ? <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      : <><Icon name="LogIn" size={16} color="white" /><span>Accéder à mon espace</span></>
                    }
                  </button>

                  <p className="text-center text-xs text-gray-400 pt-1">
                    Pas encore partenaire ?{' '}
                    <button
                      type="button"
                      onClick={() => { setMode('request'); setError(''); }}
                      className="font-semibold transition-colors"
                      style={{ color: '#78350f' }}>
                      Faire une demande
                    </button>
                  </p>
                </form>
              )}

              {/* REQUEST FORM */}
              {mode === 'request' && (
                <form onSubmit={handleAccessRequest} className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    {requestFields?.slice(0, 4)?.map(({ field, label, placeholder, icon, type, required }) => (
                      <div key={field} className={field === 'full_name' || field === 'etude_name' ? 'col-span-2' : 'col-span-1'}>
                        <label className="block text-xs font-semibold text-gray-600 mb-1.5">
                          {label}{required && <span style={{ color: '#d97706' }}> *</span>}
                        </label>
                        <div className="relative">
                          <div className="absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none">
                            <Icon name={icon} size={14} color="#9ca3af" />
                          </div>
                          <input
                            type={type}
                            value={requestForm?.[field]}
                            onChange={handleRequestChange(field)}
                            placeholder={placeholder}
                            className="w-full pl-9 pr-3 py-2.5 text-sm text-gray-900 rounded-lg transition-all duration-200 outline-none"
                            style={{ border: '1.5px solid #e5e7eb', background: '#fafafa' }}
                            onFocus={e => { e.target.style.borderColor = '#d97706'; e.target.style.background = '#fff'; e.target.style.boxShadow = '0 0 0 3px rgba(217,119,6,0.08)'; }}
                            onBlur={e => { e.target.style.borderColor = '#e5e7eb'; e.target.style.background = '#fafafa'; e.target.style.boxShadow = 'none'; }}
                          />
                        </div>
                      </div>
                    ))}
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    {requestFields?.slice(4)?.map(({ field, label, placeholder, icon, type, required }) => (
                      <div key={field}>
                        <label className="block text-xs font-semibold text-gray-600 mb-1.5">
                          {label}{required && <span style={{ color: '#d97706' }}> *</span>}
                        </label>
                        <div className="relative">
                          <div className="absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none">
                            <Icon name={icon} size={14} color="#9ca3af" />
                          </div>
                          <input
                            type={type}
                            value={requestForm?.[field]}
                            onChange={handleRequestChange(field)}
                            placeholder={placeholder}
                            className="w-full pl-9 pr-3 py-2.5 text-sm text-gray-900 rounded-lg transition-all duration-200 outline-none"
                            style={{ border: '1.5px solid #e5e7eb', background: '#fafafa' }}
                            onFocus={e => { e.target.style.borderColor = '#d97706'; e.target.style.background = '#fff'; e.target.style.boxShadow = '0 0 0 3px rgba(217,119,6,0.08)'; }}
                            onBlur={e => { e.target.style.borderColor = '#e5e7eb'; e.target.style.background = '#fafafa'; e.target.style.boxShadow = 'none'; }}
                          />
                        </div>
                      </div>
                    ))}
                  </div>

                  <button
                    type="submit"
                    disabled={loading}
                    className="w-full flex items-center justify-center space-x-2.5 py-3.5 rounded-xl text-sm font-bold text-white transition-all duration-200 mt-1 disabled:opacity-60"
                    style={{ background: 'linear-gradient(135deg, #78350f 0%, #92400e 50%, #d97706 100%)', boxShadow: '0 4px 14px rgba(120,53,15,0.35)' }}>
                    {loading
                      ? <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      : <><Icon name="Send" size={16} color="white" /><span>Soumettre ma demande</span></>
                    }
                  </button>

                  <p className="text-center text-xs text-gray-400">
                    Déjà partenaire ?{' '}
                    <button
                      type="button"
                      onClick={() => { setMode('login'); setError(''); }}
                      className="font-semibold"
                      style={{ color: '#78350f' }}>
                      Se connecter
                    </button>
                  </p>
                </form>
              )}

              {/* Back link desktop */}
              <div className="hidden lg:flex items-center justify-center mt-8 pt-6" style={{ borderTop: '1px solid #f3f4f6' }}>
                <Link to="/homepage" className="flex items-center space-x-1.5 text-xs text-gray-400 hover:text-gray-600 transition-colors">
                  <Icon name="ArrowLeft" size={13} />
                  <span>Retour à l'accueil</span>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default NotaireLoginPage;
